import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Upload } from 'lucide-react';

import type { WikiImportPlan, WikiTransfer } from '@tasks/contracts';

import {
  downloadArchive,
  importArchive,
  listTransfers,
} from '../../api/wiki';
import { queryKeys } from '../../api/queryKeys';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { PageSection } from '../../components/Page';
import { Skeleton } from '../../components/Skeleton';
import { Table, type TableColumn } from '../../components/Table';
import { useToast } from '../../components/Toast';
import { humanizeError } from '../../lib/errors';
import { WikiTemplatesSection } from './WikiTemplatesSection';
import styles from './WikiSettingsPage.module.css';

/**
 * Обмен wiki с внешним миром (docs/14 §5, §10).
 *
 * Два действия: скачать архив markdown-файлов и загрузить его обратно.
 * Импорт двухшаговый: сначала предпросмотр плана, потом применение —
 * случайная перезапись чужой правки исключена.
 */
export function WikiSettingsPage() {
  const { key = '' } = useParams<{ key: string }>();
  const toast = useToast();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [force, setForce] = useState(false);
  const [plan, setPlan] = useState<WikiImportPlan | null>(null);

  const transfersQuery = useQuery({
    queryKey: queryKeys.projects.wikiTransfers(key),
    queryFn: ({ signal }) => listTransfers(key, signal),
    enabled: Boolean(key),
  });

  const exportMutation = useMutation({
    mutationFn: () => downloadArchive(key),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.projects.wikiTransfers(key) });
      toast.success('Архив выгружен');
    },
    onError: (error: unknown) => toast.error(humanizeError(error).message),
  });

  const previewMutation = useMutation({
    mutationFn: (target: File) => importArchive(key, target, { dryRun: true, force }),
    onSuccess: (result) => {
      setPlan(result.plan);
      toast.success('План импорта готов: проверьте изменения');
    },
    onError: (error: unknown) => toast.error(humanizeError(error).message),
  });

  const applyMutation = useMutation({
    mutationFn: (target: File) => importArchive(key, target, { dryRun: false, force }),
    onSuccess: async (result) => {
      setPlan(null);
      setFile(null);
      if (fileInput.current) fileInput.current.value = '';
      await queryClient.invalidateQueries({ queryKey: queryKeys.projects.wikiTransfers(key) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.projects.wikiPages(key) });
      toast.success(
        result.transfer
          ? `Импорт применён: создано ${result.transfer.pagesCreated}, обновлено ${result.transfer.pagesUpdated}`
          : 'Импорт завершён: изменений нет',
      );
    },
    onError: (error: unknown) => toast.error(humanizeError(error).message),
  });

  const applyable = plan !== null && plan.created + plan.updated > 0;

  return (
    <>
      <WikiTemplatesSection projectKey={key} />

      <PageSection title="Экспорт">
        <p className={styles['hint']}>
          Архив содержит опубликованные страницы: один файл <code>.md</code> на страницу
          с метаданными в начале. Черновики не выгружаются.
        </p>
        <Button
          onClick={() => exportMutation.mutate()}
          loading={exportMutation.isPending}
          icon={<Download size={16} />}
        >
          Скачать архив
        </Button>
      </PageSection>

      <PageSection title="Импорт">
        <p className={styles['hint']}>
          Загрузите ZIP с markdown-файлами или отдельный <code>.md</code>. Сначала
          показывается план: что будет создано, обновлено и пропущено. Применение
          атомарно — при ошибке не меняется ничего.
        </p>

        <input
          ref={fileInput}
          type="file"
          accept=".zip,.md"
          aria-label="Файл архива"
          onChange={(event) => {
            const next = event.target.files?.[0] ?? null;
            setFile(next);
            setPlan(null);
          }}
        />

        <label className={styles['checkbox']}>
          <input
            type="checkbox"
            checked={force}
            onChange={(event) => {
              setForce(event.target.checked);
              setPlan(null);
            }}
          />
          Перезаписывать страницы, изменённые после экспорта
        </label>

        <div className={styles['actions']}>
          <Button
            onClick={() => file && previewMutation.mutate(file)}
            disabled={file === null}
            loading={previewMutation.isPending}
            icon={<Upload size={16} />}
          >
            Проверить архив
          </Button>
          <Button
            onClick={() => file && applyMutation.mutate(file)}
            disabled={!applyable}
            loading={applyMutation.isPending}
          >
            Применить
          </Button>
        </div>

        {plan ? <ImportPlanTable plan={plan} /> : null}
      </PageSection>

      <PageSection title="Журнал обмена">
        {transfersQuery.isLoading ? (
          <Skeleton height="120px" />
        ) : (transfersQuery.data ?? []).length === 0 ? (
          <EmptyState
            icon={<Download size={20} />}
            title="Обмен ещё не выполнялся"
            description="Здесь появятся импорты и экспорты архива."
            compact
          />
        ) : (
          <Table
            ariaLabel="Журнал обмена"
            columns={TRANSFER_COLUMNS}
            rows={transfersQuery.data ?? []}
            getRowId={(transfer) => transfer.id}
          />
        )}
      </PageSection>
    </>
  );
}

function ImportPlanTable({ plan }: { readonly plan: WikiImportPlan }) {
  return (
    <div className={styles['plan']}>
      <p className={styles['planSummary']}>
        Всего файлов: {plan.total}. Создать: {plan.created}, обновить: {plan.updated}, пропустить:{' '}
        {plan.skipped}, конфликтов: {plan.conflict}, ошибок: {plan.invalid}.
      </p>

      {plan.conflict > 0 && plan.updated === 0 ? (
        <p className={styles['warning']}>
          Страницы изменены после экспорта — они не будут перезаписаны. Включите
          перезапись, если хотите применить архив силой.
        </p>
      ) : null}

      <Table
        ariaLabel="План импорта"
        columns={PLAN_COLUMNS}
        rows={planRows(plan)}
        getRowId={(row) => row.id}
      />
    </div>
  );
}

interface PlanRow {
  readonly id: string;
  readonly path: string;
  readonly title: string;
  readonly action: string;
  readonly reason: string;
}

const PLAN_COLUMNS: readonly TableColumn<PlanRow>[] = [
  { id: 'path', header: 'Файл', render: (row) => <code>{row.path}</code> },
  { id: 'title', header: 'Заголовок', render: (row) => row.title },
  { id: 'action', header: 'Действие', render: (row) => row.action },
  { id: 'reason', header: 'Причина', render: (row) => row.reason },
];

function planRows(plan: WikiImportPlan): PlanRow[] {
  return [
    ...plan.pages.map((item, index) => ({
      id: `${item.path}-${index}`,
      path: item.path,
      title: item.title,
      action: actionLabel(item.action),
      reason: item.reason ?? '—',
    })),
    ...plan.errors.map((item, index) => ({
      id: `error-${item.path}-${index}`,
      path: item.path,
      title: '—',
      action: actionLabel('invalid'),
      reason: item.reason,
    })),
  ];
}

const TRANSFER_COLUMNS: readonly TableColumn<WikiTransfer>[] = [
  { id: 'created', header: 'Когда', render: (transfer) => new Date(transfer.createdAt).toLocaleString('ru-RU') },
  { id: 'direction', header: 'Операция', render: (transfer) => (transfer.direction === 'import' ? 'Импорт' : 'Экспорт') },
  { id: 'file', header: 'Файл', render: (transfer) => transfer.fileName || '—' },
  { id: 'createdPages', header: 'Создано', render: (transfer) => transfer.pagesCreated },
  { id: 'updated', header: 'Обновлено', render: (transfer) => transfer.pagesUpdated },
  { id: 'skipped', header: 'Пропущено', render: (transfer) => transfer.pagesSkipped },
  { id: 'conflicts', header: 'Конфликты', render: (transfer) => transfer.pagesConflict },
  {
    id: 'status',
    header: 'Итог',
    render: (transfer) =>
      transfer.status === 'ok' ? <Badge tone="success">ок</Badge> : <Badge tone="danger">ошибка</Badge>,
  },
];

function actionLabel(action: string): string {
  if (action === 'create') return 'создать';
  if (action === 'update') return 'обновить';
  if (action === 'skip') return 'пропустить';
  if (action === 'conflict') return 'конфликт';
  return 'ошибка';
}
